import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../ui/Modal';
import { badgeClass, formatNumber, getApiErrorMessage, inputClass, inputErrorClass, primaryButtonClass, secondaryButtonClass } from '../ui/FormControls';
import { cn } from '../../lib/utils';

type Tab = 'components' | 'explosion';

export interface StoredLine {
  componentId: string;
  sku: string;
  name: string;
  unit: string;
  quantity: number;
  scrapPercentage: number;
  isPhantom: boolean;
  isOptional: boolean;
  alternativeGroup: string | null;
  alternativeRank: number;
}
interface Structure {
  id: string;
  bomNumber: string;
  productId: string;
  productName: string | null;
  sku: string | null;
  unit: string | null;
  baseQuantity: number;
  status: string;
  lines: StoredLine[];
}
// A line as it is being edited: numbers stay text until they are saved
interface EditLine {
  componentId: string;
  sku: string;
  name: string;
  unit: string;
  quantity: string;
  scrapPercentage: string;
  isPhantom: boolean;
  isOptional: boolean;
  alternativeGroup: string;
  alternativeRank: string;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
}

export interface ExplosionNode {
  level: number;
  lineNumber: number;
  productId: string;
  sku: string;
  name: string;
  unit: string;
  quantityPer: number;
  required: number;
  kind: 'material' | 'subassembly' | 'phantom';
  counted: boolean;
  note: 'alternate' | 'optional' | null;
  bomNumber: string | null;
  onHand: number;
  children: ExplosionNode[];
}
export interface Requirement {
  productId: string;
  sku: string;
  name: string;
  unit: string;
  required: number;
  onHand: number;
  short: number;
}
export interface Explosion {
  bom: { id: string; bomNumber: string; productId: string; productName: string; sku: string; unit: string; baseQuantity: number; status: string };
  quantity: number;
  usable: boolean;
  warnings: string[];
  tree: ExplosionNode[];
  issueList: Requirement[];
  rawMaterials: Requirement[];
}

export const engineeringBomApi = {
  usable: async (productId: string): Promise<{ id: string; bomNumber: string; version: number; revision: string; status: string; lines: number }[]> =>
    (await api.get('/manufacturing/engineering-boms', { params: { productId } })).data,
  lines: async (id: string): Promise<Structure> => (await api.get(`/manufacturing/engineering-boms/${id}/lines`)).data,
  explosion: async (id: string, quantity: number, date?: string): Promise<Explosion> =>
    (await api.get(`/manufacturing/engineering-boms/${id}/explosion`, { params: { quantity, ...(date ? { date } : {}) } })).data,
  saveLines: async (id: string, lines: object[]) => (await api.patch(`/master-data/bom-headers/${id}`, { lines })).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

const thClass = 'px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const KIND: Record<ExplosionNode['kind'], { label: string; className: string }> = {
  material: { label: 'Material', className: 'bg-gray-50 text-gray-700 ring-gray-500/20' },
  subassembly: { label: 'Sub-assembly', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  phantom: { label: 'Phantom', className: 'bg-purple-50 text-purple-700 ring-purple-600/20' },
};

// ---------------------------------------------------------------- requirements, also used on the work order form

export const RequirementTable: React.FC<{ rows: Requirement[]; empty: string }> = ({ rows, empty }) =>
  rows.length === 0 ? (
    <p className="px-3 py-3 text-sm text-gray-500">{empty}</p>
  ) : (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th scope="col" className={thClass}>Item</th>
          <th scope="col" className={cn(thClass, 'text-right')}>Required</th>
          <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
          <th scope="col" className={cn(thClass, 'text-right')}>Short</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {rows.map((row) => (
          <tr key={row.productId}>
            <td className="px-3 py-1.5 text-sm text-gray-900">
              {row.name} <span className="text-xs text-gray-500">{row.sku}</span>
            </td>
            <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-900 tabular-nums">
              {formatNumber(row.required)} <span className="text-gray-500">{row.unit}</span>
            </td>
            <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(row.onHand)}</td>
            <td className={cn('whitespace-nowrap px-3 py-1.5 text-right text-sm tabular-nums', row.short > 0 ? 'font-medium text-red-600' : 'text-gray-400')}>{row.short > 0 ? formatNumber(row.short) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

const flatten = (nodes: ExplosionNode[]): ExplosionNode[] => nodes.flatMap((node) => [node, ...flatten(node.children)]);

// ---------------------------------------------------------------- the explosion

const ExplosionTab: React.FC<{ bomId: string; baseQuantity: number }> = ({ bomId, baseQuantity }) => {
  const [quantityInput, setQuantityInput] = useState(String(baseQuantity || 1));
  const [quantity, setQuantity] = useState(baseQuantity || 1);
  useEffect(() => {
    const timer = setTimeout(() => {
      const value = Number(quantityInput);
      if (Number.isFinite(value) && value > 0) setQuantity(value);
    }, 300);
    return () => clearTimeout(timer);
  }, [quantityInput]);

  const { data, isLoading, isError, error } = useQuery({ queryKey: ['engineeringBoms', 'explosion', bomId, quantity], queryFn: () => engineeringBomApi.explosion(bomId, quantity) });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="bx-quantity" className="block text-sm font-medium text-gray-700">
            Quantity to make
          </label>
          <input id="bx-quantity" type="number" min="0.0001" step="0.0001" value={quantityInput} onChange={(event) => setQuantityInput(event.target.value)} className={cn(inputClass, 'mt-1 w-40 text-right tabular-nums')} />
        </div>
        {data && (
          <p className="pb-2 text-sm text-gray-600">
            of {data.bom.productName} ({data.bom.unit})
          </p>
        )}
      </div>

      {isError ? (
        <p className="rounded-md bg-red-50 px-4 py-3 text-sm text-red-800 ring-1 ring-red-200">{getApiErrorMessage(error, 'The BOM could not be exploded.')}</p>
      ) : isLoading || !data ? (
        <p className="py-8 text-center text-sm text-gray-500">Working it out…</p>
      ) : (
        <>
          {data.warnings.map((warning) => (
            <p key={warning} className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
              {warning}
            </p>
          ))}

          <section className="overflow-hidden rounded-lg border border-gray-200">
            <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Structure, level by level</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className={thClass}>Component</th>
                    <th scope="col" className={thClass}>Kind</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Per parent</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>Required</th>
                    <th scope="col" className={cn(thClass, 'text-right')}>In stock</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {flatten(data.tree).length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-sm text-gray-500">This BOM has no components yet. Add them on the Components tab.</td>
                    </tr>
                  ) : (
                    flatten(data.tree).map((node, index) => (
                      <tr key={`${node.productId}-${index}`} className={cn(!node.counted && 'text-gray-400')}>
                        <td className="px-3 py-1.5 text-sm" style={{ paddingLeft: `${0.75 + (node.level - 1) * 1.5}rem` }}>
                          <span className={cn('font-medium', node.counted ? 'text-gray-900' : 'text-gray-400')}>{node.name}</span> <span className="text-xs text-gray-500">{node.sku}</span>
                          {node.bomNumber && <span className="ml-2 text-xs text-gray-500">via {node.bomNumber}</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5">
                          <span className={cn(badgeClass, KIND[node.kind].className)}>{KIND[node.kind].label}</span>
                          {node.note && <span className={cn(badgeClass, 'ml-1 bg-amber-50 text-amber-800 ring-amber-600/20')}>{node.note === 'alternate' ? 'Alternate, not counted' : 'Optional, not counted'}</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm tabular-nums">{formatNumber(node.quantityPer, 6)}</td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm font-medium tabular-nums">
                          {formatNumber(node.required, 6)} <span className="font-normal text-gray-500">{node.unit}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-1.5 text-right text-sm tabular-nums">{formatNumber(node.onHand)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="overflow-hidden rounded-lg border border-gray-200">
              <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Issued to a work order</h4>
              <p className="border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500">Direct components. A phantom is replaced by its own; a sub-assembly is issued as itself.</p>
              <RequirementTable rows={data.issueList} empty="Nothing to issue." />
            </section>
            <section className="overflow-hidden rounded-lg border border-gray-200">
              <h4 className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Bought materials in all</h4>
              <p className="border-b border-gray-100 px-3 py-1.5 text-xs text-gray-500">Everything exploded to the bottom, as if every sub-assembly were made for this quantity.</p>
              <RequirementTable rows={data.rawMaterials} empty="No materials." />
            </section>
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------- the components

// What the editor needs to know about the item whose components it edits
export interface EditorSubject {
  productId: string;
  productName: string | null;
  unit: string | null;
  baseQuantity: number;
  lines: StoredLine[];
}

interface ComponentLinesEditorProps {
  structure: EditorSubject;
  canEdit: boolean;
  isPending: boolean;
  saveLabel: string;
  // Called with the whole list, in the shape the API takes
  onSave: (lines: object[]) => void;
}

// The components of an item as an editable table: used for a draft BOM and for the proposal of an engineering change
export const ComponentLinesEditor: React.FC<ComponentLinesEditorProps> = ({ structure, canEdit, isPending, saveLabel, onSave }) => {
  const [lines, setLines] = useState<EditLine[]>(() =>
    structure.lines.map((line) => ({
      componentId: line.componentId,
      sku: line.sku,
      name: line.name,
      unit: line.unit,
      quantity: String(line.quantity),
      scrapPercentage: String(line.scrapPercentage),
      isPhantom: line.isPhantom,
      isOptional: line.isOptional,
      alternativeGroup: line.alternativeGroup ?? '',
      alternativeRank: String(line.alternativeRank),
    })),
  );
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: found = [], isFetching } = useQuery({ queryKey: ['engineeringBoms', 'products', search], queryFn: () => engineeringBomApi.products(search), enabled: canEdit && search.length >= 2 });
  const choices = found.filter((product) => product.id !== structure.productId);

  const change = (index: number, patch: Partial<EditLine>) => {
    setLines((prev) => prev.map((line, position) => (position === index ? { ...line, ...patch } : line)));
    setErrors((prev) => {
      const { [index]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const add = (product: ProductChoice) => {
    setLines((prev) => [...prev, { componentId: product.id, sku: product.sku, name: product.name, unit: product.unit, quantity: '1', scrapPercentage: '0', isPhantom: false, isOptional: false, alternativeGroup: '', alternativeRank: '1' }]);
    setSearchInput('');
    setSearch('');
  };

  const handleSave = () => {
    const next: Record<number, string> = {};
    lines.forEach((line, index) => {
      const quantity = Number(line.quantity);
      const scrap = Number(line.scrapPercentage || 0);
      const rank = Number(line.alternativeRank || 1);
      if (!Number.isFinite(quantity) || quantity < 0.001) next[index] = 'Enter a quantity of at least 0.001';
      else if (!Number.isFinite(scrap) || scrap < 0 || scrap >= 100) next[index] = 'Scrap is a percentage from 0 to under 100';
      else if (!Number.isInteger(rank) || rank < 1) next[index] = 'The choice is a whole number from 1';
    });
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSave(
      lines.map((line, index) => ({
        componentId: line.componentId,
        quantity: Number(line.quantity),
        scrapPercentage: Number(line.scrapPercentage || 0),
        position: (index + 1) * 10,
        isPhantom: line.isPhantom,
        isOptional: line.isOptional,
        ...(line.alternativeGroup.trim() ? { alternativeGroup: line.alternativeGroup.trim(), alternativeRank: Number(line.alternativeRank || 1) } : {}),
      })),
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        What goes into {formatNumber(structure.baseQuantity)} {structure.unit ?? ''} of {structure.productName ?? 'the item'}.
      </p>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Component</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Scrap %</th>
              <th scope="col" className={thClass} title="Never stocked: its own components are issued in its place">Phantom</th>
              <th scope="col" className={thClass} title="Shown on the BOM, not issued to a work order">Optional</th>
              <th scope="col" className={thClass} title="Lines with the same group are alternates; only choice 1 is required">Alt. group</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Choice</th>
              <th scope="col" className={thClass}><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lines.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-sm text-gray-500">No components yet. Search below to add the first.</td>
              </tr>
            ) : (
              lines.map((line, index) => (
                <React.Fragment key={`${line.componentId}-${index}`}>
                  <tr>
                    <td className="px-3 py-1.5 text-sm text-gray-900">
                      {line.name} <span className="text-xs text-gray-500">{line.sku}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" min="0.001" step="0.001" disabled={!canEdit} value={line.quantity} onChange={(event) => change(index, { quantity: event.target.value })} aria-label={`Quantity of ${line.name}`} className={cn(inputClass, 'w-28 py-1 text-right tabular-nums', errors[index] && inputErrorClass)} />
                      <span className="ml-1 text-xs text-gray-500">{line.unit}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" min="0" max="99" step="0.01" disabled={!canEdit} value={line.scrapPercentage} onChange={(event) => change(index, { scrapPercentage: event.target.value })} aria-label={`Scrap percentage of ${line.name}`} className={cn(inputClass, 'w-20 py-1 text-right tabular-nums')} />
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="checkbox" disabled={!canEdit} checked={line.isPhantom} onChange={(event) => change(index, { isPhantom: event.target.checked })} aria-label={`${line.name} is a phantom`} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="checkbox" disabled={!canEdit} checked={line.isOptional} onChange={(event) => change(index, { isOptional: event.target.checked })} aria-label={`${line.name} is optional`} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    </td>
                    <td className="px-3 py-1.5">
                      <input type="text" maxLength={50} disabled={!canEdit} value={line.alternativeGroup} onChange={(event) => change(index, { alternativeGroup: event.target.value })} aria-label={`Alternate group of ${line.name}`} className={cn(inputClass, 'w-24 py-1')} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <input type="number" min="1" step="1" disabled={!canEdit || !line.alternativeGroup.trim()} value={line.alternativeRank} onChange={(event) => change(index, { alternativeRank: event.target.value })} aria-label={`Choice of ${line.name}`} className={cn(inputClass, 'w-16 py-1 text-right tabular-nums')} />
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {canEdit && (
                        <button type="button" onClick={() => setLines((prev) => prev.filter((_, position) => position !== index))} aria-label={`Remove ${line.name}`} className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600">
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                  {errors[index] && (
                    <tr>
                      <td colSpan={8} className="px-3 pb-2 text-sm text-red-600">{errors[index]}</td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canEdit && (
        <div>
          <div className="relative">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input id="bc-search" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU to add as a component" aria-label="Search products to add" className={cn(inputClass, 'pl-9')} />
          </div>
          {search.length >= 2 && (
            <ul className="mt-1 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {choices.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
              ) : (
                choices.map((product) => (
                  <li key={product.id}>
                    <button type="button" onClick={() => add(product)} className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50">
                      <span>
                        <span className="font-medium text-gray-900">{product.name}</span> <span className="text-gray-500">{product.sku}</span>
                      </span>
                      <span className="text-xs text-gray-500">{product.unit}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button type="button" onClick={handleSave} disabled={isPending} className={primaryButtonClass}>
            {isPending ? 'Saving…' : saveLabel}
          </button>
        </div>
      )}
    </div>
  );
};

const ComponentsTab: React.FC<{ structure: Structure; canEdit: boolean; onSaved: () => void }> = ({ structure, canEdit, onSaved }) => {
  const save = useMutation({
    mutationFn: (payload: object[]) => engineeringBomApi.saveLines(structure.id, payload),
    onSuccess: () => {
      toast.success(`Components of ${structure.bomNumber} saved`);
      onSaved();
    },
    onError: (saveError) => toast.error(getApiErrorMessage(saveError, 'The components could not be saved')),
  });
  // Once a BOM is approved it is under change control
  const released = structure.status !== 'draft';
  return (
    <div className="space-y-4">
      {released && (
        <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          {structure.bomNumber} is {structure.status}, so its components are changed through an engineering change note.{' '}
          {(structure.status === 'approved' || structure.status === 'active') && (
            <Link to={`/master-data/ecns?bom=${structure.id}`} className="font-semibold underline">
              Raise a change note
            </Link>
          )}
        </p>
      )}
      <ComponentLinesEditor structure={structure} canEdit={canEdit && !released} isPending={save.isPending} saveLabel="Save Components" onSave={(payload) => save.mutate(payload)} />
    </div>
  );
};

// ---------------------------------------------------------------- the window

interface BomStructureModalProps {
  bomId: string | null;
  canEdit: boolean;
  onClose: () => void;
}

// The components of an engineering BOM, and what a quantity of the product needs
const BomStructureModal: React.FC<BomStructureModalProps> = ({ bomId, canEdit, onClose }) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('components');
  useEffect(() => setTab('components'), [bomId]);
  const { data, isLoading, isError, error, dataUpdatedAt } = useQuery({ queryKey: ['engineeringBoms', 'lines', bomId], queryFn: () => engineeringBomApi.lines(bomId as string), enabled: !!bomId });

  return (
    <Modal isOpen={!!bomId} onClose={onClose} title={data ? `${data.bomNumber} · ${data.productName ?? ''}` : 'BOM'} size="2xl">
      {isError ? (
        <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The BOM could not be loaded.')}</p>
      ) : isLoading || !data ? (
        <p className="py-10 text-center text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="space-y-4">
          <div className="flex gap-1 border-b border-gray-200" role="tablist">
            {([{ key: 'components', label: `Components · ${data.lines.length}` }, { key: 'explosion', label: 'Explosion' }] as { key: Tab; label: string }[]).map((item) => (
              <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                {item.label}
              </button>
            ))}
          </div>
          {tab === 'components' ? (
            <ComponentsTab
              key={dataUpdatedAt}
              structure={data}
              canEdit={canEdit}
              onSaved={() => ['engineeringBoms', 'bomHeaders', 'bomHeadersGroupedType', 'bomHeadersGroupedStatus'].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }))}
            />
          ) : (
            <ExplosionTab bomId={data.id} baseQuantity={data.baseQuantity} />
          )}
          <div className="flex justify-end border-t border-gray-200 pt-3">
            <button type="button" onClick={onClose} className={secondaryButtonClass}>
              Close
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BomStructureModal;
